import { createColumnHelper } from "@tanstack/react-table";
import { capitalCase } from "change-case";
import { useMemo } from "react";

import { formCategoryLabel, formTypeTheme } from "@smart/bridge-types-basic";
import { useStorage } from "@smart/itops-hooks-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import {
  Badge,
  CellWrapper,
  TableProps,
  TimePeriod,
  TimeRange,
} from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { formatDateTime } from "@smart/itops-utils-basic";

import {
  getOnRowClick,
  filterByDatePeriod,
  filterBySelection,
  getStaffName,
  sortSchema,
  sortByStaffName,
  typeOfMatterFilterOptions,
  typeOfMatterFilterAllOption,
  filterByTypeOfMatter,
} from "./column-helpers";
import { useTimeFilterValue } from "./time-filter-value";
import { ExtendedGqlSubmission, GqlSubmission } from "../../types";

export const useCompletedSubmissionsColumns = () => {
  const { terms, dateDisplayFormat } = loadLocale();
  const [columnSort, setColumnSort] = useStorage({
    defaultValue: [{ id: "completedOn", desc: true }],
    key: "completed-list-sort",
    schema: sortSchema,
    storage: "local",
  });
  const { staff, host } = useSmokeballApp();
  const { timeFilter, setTimeFilter } = useTimeFilterValue({
    filterStorageKey: "completedOn-date-filter",
  });

  const columns = useMemo(() => {
    const col = createColumnHelper<ExtendedGqlSubmission>();

    return [
      col.accessor("form.values.title", {
        header: "Form Name",
        enableColumnFilter: false,
        meta: { showRowCount: true },
        cell: ({ getValue }) => (
          <CellWrapper label={getValue()} size="medium" />
        ),
      }),
      col.accessor("typeOfMatter", {
        id: "matter-category",
        header: "Type",
        meta: {
          filter: {
            type: "radio",
            name: "matterCategory",
            title: "Type (Lead/Matter)",
            order: 1,
            fixedOptions: typeOfMatterFilterOptions,
            defaultOption: typeOfMatterFilterAllOption,
          },
        },
        cell: ({ getValue }) => (
          <Badge
            label={getValue() && formCategoryLabel[getValue()!]}
            theme={getValue() && formTypeTheme[getValue()!]}
          />
        ),
        filterFn: filterByTypeOfMatter,
      }),
      col.accessor("matter.title", {
        header: "Matter",
        enableColumnFilter: false,
        cell: ({ getValue }) => <CellWrapper label={getValue() || ""} />,
      }),
      col.accessor("matter.personResponsibleStaffId", {
        id: "personResponsibleStaffId",
        header: terms.responsiblePerson.header,
        meta: {
          filter: {
            type: "select",
            name: "personResponsible",
            title: capitalCase(terms.responsiblePerson.singular),
            order: 2,
            placeholder: "All",
            fixedOptions: [
              { value: "", label: "Unassigned" },
              ...(
                staff?.current?.map((s) => ({
                  value: s.id,
                  label: getStaffName({ staff: staff?.current, staffId: s.id }),
                })) || []
              ).sort((a, b) => a.label.localeCompare(b.label)),
            ],
          },
        },
        cell: ({ getValue }) => (
          <CellWrapper
            label={getStaffName({ staff: staff?.current, staffId: getValue() })}
            hideTooltip={!getValue()}
          />
        ),
        filterFn: filterBySelection,
        sortingFn: (rowA, rowB) =>
          sortByStaffName({
            staff: staff?.current,
            staffAId: rowA.original.matter?.personResponsibleStaffId,
            staffBId: rowB.original.matter?.personResponsibleStaffId,
          }),
      }),
      col.accessor("values.completedAt", {
        header: "Completed On",
        id: "completedOn",
        meta: {
          filter: {
            type: "dateRange",
            name: "completedOn",
            title: "Completed",
            dateRangeExcludedOptions: ["allTime"],
            onFilterValueChanged: (value) => {
              const valueAsTimeFilter = value as unknown as {
                period: TimePeriod;
                range?: TimeRange;
              };
              if (valueAsTimeFilter) {
                setTimeFilter(
                  valueAsTimeFilter.period,
                  valueAsTimeFilter.range,
                );
              }
            },
          },
        },
        sortDescFirst: true,
        cell: ({ getValue }) => (
          <CellWrapper
            label={formatDateTime(getValue() ?? undefined, dateDisplayFormat)}
          />
        ),
        filterFn: filterByDatePeriod,
      }),
    ];
  }, [staff?.current?.length]);

  const select: TableProps<GqlSubmission>["select"] = {
    state: {},
    onChange: (selected) => {
      const value = typeof selected === "function" ? selected({}) : selected;
      console.debug("selected row: ", value);
    },
  };

  const sort: TableProps<GqlSubmission>["sort"] = {
    state: columnSort,
    onChange: setColumnSort,
  };

  return {
    columns,
    select,
    sort,
    timeFilter,
    setTimeFilter,
    onRowClick: getOnRowClick(host?.navigate),
  };
};
